// The invoice pay page is served standalone (not inside the live site runtime),
// so no visitor session is created and the cashier widget would otherwise be
// authenticated with a server-fetched app instance that carries no person
// identity. Captcha validation then rejects the charge because no identity is
// present in the request.
//
// Fetching `/_api/v1/access-tokens` on the site origin mints an `svSession` and
// returns a visitor-bound app instance (its `aid` is the site visitor id). Using
// that instance to authenticate the cashier widget gives the charge request a
// person identity, which is what captcha requires.

export const INVOICES_APP_DEF_ID = '13ee94c1-b635-8505-3391-97919052c16f';

// Both pay clients wait for this request before mounting the cashier widget, so
// it is bounded: a stalled request must fall back rather than leave the page blank.
export const VISITOR_INSTANCE_TIMEOUT_MS = 5000;

export type VisitorInstance = {
  appInstance: string;
  visitorId?: string;
};

type AccessTokensResponse = {
  visitorId?: string;
  apps?: Record<string, { instance?: string } | undefined>;
};

type FetchLike = (
  input: string,
  init?: { credentials?: 'include'; signal?: AbortSignal },
) => Promise<{ ok: boolean; json: () => Promise<AccessTokensResponse> }>;

// Derives the site origin + path (e.g. `https://user.wixsite.com/my-site`) from a
// URL that still carries the site path, such as the invoice `quoteUrl`. The pay
// page itself can be served from the bare origin, so its own `location` is not a
// reliable source for the site path.
export const getSiteBaseUrl = (urlWithSitePath: string): string => {
  const url = new URL(urlWithSitePath);
  const apiIndex = url.pathname.indexOf('/_api');
  const path = apiIndex >= 0 ? url.pathname.slice(0, apiIndex) : url.pathname;
  const sitePath = path.replace(/\/$/, '');
  return `${url.origin}${sitePath}`;
};

export const fetchVisitorInstance = async (
  urlWithSitePath: string,
  fetchImpl: FetchLike = fetch,
  timeoutMs: number = VISITOR_INSTANCE_TIMEOUT_MS,
): Promise<VisitorInstance | null> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve(null);
    }, timeoutMs);
  });

  const request = async (): Promise<VisitorInstance | null> => {
    const siteBaseUrl = getSiteBaseUrl(urlWithSitePath);
    const response = await fetchImpl(`${siteBaseUrl}/_api/v1/access-tokens`, {
      credentials: 'include',
      signal: controller.signal,
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const appInstance = data?.apps?.[INVOICES_APP_DEF_ID]?.instance;
    if (!appInstance) {
      return null;
    }
    return { appInstance, visitorId: data.visitorId };
  };

  try {
    // The race also bounds reading the body, which the abort signal alone may not.
    return await Promise.race([request(), timeout]);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};
